/**
 * Footer component for all pages.
 *
 * Displays:
 * - Copyright for all users
 * - Settings button for users with server session (guest or authenticated)
 * - Legal links (Privacy, Terms)
 */

import { DEVELOPER_NAME } from '@idle-depths/shared';
import { Settings } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router';

import { Button } from '@/components/ui/Button';
import { useStores } from '@/stores/StoreContext';

/**
 * Footer component.
 */
export const Footer = observer(function Footer(): React.ReactElement {
  const { authStore } = useStores();
  const currentYear = new Date().getFullYear();
  // Show settings for any user with a server session (guest or authenticated)
  const hasServerUser = authStore.hasServerUser;

  return (
    <footer className="fixed bottom-0 left-0 right-0 z-50 py-4">
      <div className="container mx-auto flex items-center justify-between gap-2 px-4">
        {/* Copyright (left side) */}
        <p className="text-sm text-muted-foreground">
          © {currentYear} {DEVELOPER_NAME}
        </p>

        {/* Right side: Legal links + Settings button for users with server session */}
        <div className="flex items-center gap-4">
          <nav className="flex gap-4">
            <Link
              to="/privacy"
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              Terms
            </Link>
          </nav>

          {/* Settings button for users with server session (guest or authenticated) */}
          {hasServerUser && (
            <Link to="/account">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label="Account settings"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </footer>
  );
});
